import { useEphemeralAppState } from "providers/WithEphemeralAppState";
import { useRoutePattern } from "utils/routes";

import { Data, SeriesConfig } from "./util";

export type DisabledSeries = { [key: string]: boolean };

// Which legend series are toggled off, kept in ephemeral (in-memory) app state so a
// toggle survives navigation within the SPA but resets on reload. Keyed by route +
// series signature (mirroring Grid's useColumnWidths): on a different series set the key
// differs and we fall back to defaults. Empty series are always disabled regardless of
// any stored value, mirroring how GridImpl forces fixed columns over a saved width.
export function useSeriesSelection(
  series: SeriesConfig[],
  data: Data,
): [DisabledSeries, (key: string) => void] {
  const routePattern = useRoutePattern();
  const seriesKeys = series.map((s) => s.key);
  const key = `graph-${routePattern}-${seriesKeys.join(",")}`;
  const [stored, setStored] = useEphemeralAppState<DisabledSeries>(key);

  // Defaults, as before: series.defaultDisabled, plus empty series start disabled.
  const defaults: DisabledSeries = series.reduce(
    (map, s) => ({
      ...map,
      [s.key]: s.defaultDisabled || data[s.key].length === 0,
    }),
    {},
  );

  // Accept a stored value only if its keys line up with the current series set
  // (analog of GridContext's length check) — otherwise fall back to defaults.
  const storedValid =
    !!stored &&
    Object.keys(stored).length === seriesKeys.length &&
    seriesKeys.every((k) => k in stored);

  const seriesDisabled: DisabledSeries = seriesKeys.reduce(
    (map, k) => ({
      ...map,
      [k]: data[k].length === 0 ? true : storedValid ? stored![k] : defaults[k],
    }),
    {},
  );

  const toggle = (k: string) => {
    setStored({ ...seriesDisabled, [k]: !seriesDisabled[k] });
  };

  return [seriesDisabled, toggle];
}
